import React from "react";
import InputField from "../InputField";
import DateField from "../DateField";

const MAX_QUANTITY = 999;

const formatPrice = (product) => {
  if (product.price === null || product.price === undefined) return "Price on request";
  return `${product.price} ${product.currency || "SEK"} excl. VAT`;
};

const clampQuantity = (raw) => {
  const number = Number.parseInt(raw, 10);
  if (Number.isNaN(number) || number < 0) return 0;
  return Math.min(number, MAX_QUANTITY);
};

/**
 * Product cards from the customer's assortment with a quantity per line (PBIs #304-#306), plus
 * delivery date, on-site help and delivery details (PBI #307), and free text for anything the
 * short catalogue does not carry (#365). Only what the API says the customer may order is shown;
 * the API resolves names and prices again when it builds the mail. The cards are deliberately
 * not clickable: there is no product detail view to argue specifications over.
 */
const HardwareOrderSection = ({ products, loading, order, onOrderChange }) => {
  const setField = (field, value) => onOrderChange({ ...order, [field]: value });
  const setQuantity = (productPublicId, raw) => {
    const quantity = clampQuantity(raw);
    const items = { ...order.items };
    if (quantity === 0) delete items[productPublicId];
    else items[productPublicId] = quantity;
    onOrderChange({ ...order, items });
  };

  const categories = [...new Set(products.map((p) => p.category || "Other"))];

  return (
    <div className="flex flex-col gap-4 border-l-2 border-mute-200 pl-4">
      <h3 className="font-bold">New hardware</h3>

      {loading && <p className="text-sm text-mute-400">Loading your assortment...</p>}

      {!loading && products.length === 0 && (
        <p className="text-sm">
          No products are set up for your billing group yet. Describe what you need in the comment
          field and Diflex will get back to you.
        </p>
      )}

      {categories.map((category) => (
        <div key={category} className="flex flex-col gap-2">
          <h4 className="text-sm font-bold text-mute-600">{category}</h4>
          {products
            .filter((p) => (p.category || "Other") === category)
            .map((product) => {
              const quantity = order.items[product.hardwareProductPublicId] || 0;
              return (
                <div
                  key={product.hardwareProductPublicId}
                  className={`flex gap-4 border rounded-md p-3 ${quantity > 0 ? "border-primary-600 bg-blue-50" : ""}`}
                >
                  {product.imageUrl?.startsWith("https://") && (
                    <img src={product.imageUrl} alt="" className="w-20 h-20 object-contain flex-none" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{product.name}</div>
                    {product.articleNumber && <div className="text-xs text-mute-400">Art. {product.articleNumber}</div>}
                    {product.specification && <div className="text-sm whitespace-pre-line">{product.specification}</div>}
                    {product.description && <div className="text-sm text-mute-600 whitespace-pre-line">{product.description}</div>}
                    <div className="text-sm font-bold mt-1">{formatPrice(product)}</div>
                  </div>
                  <div className="w-24 flex-none">
                    <InputField
                      label="Qty"
                      type="number"
                      value={quantity || ""}
                      placeholder="0"
                      onChange={(e) => setQuantity(product.hardwareProductPublicId, e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      ))}

      {products.length > 0 && (
        <InputField
          label="Other hardware requests"
          value={order.otherRequests || ""}
          onChange={(e) => setField("otherRequests", e.target.value)}
          placeholder="Anything not in the catalogue, e.g. a specific model or accessory"
          type="text-area"
        />
      )}

      {/* Deliberately unvalidated (#365): any date, or none, may be asked for. */}
      <DateField
        label="Desired delivery date"
        value={order.desiredDeliveryDate || ""}
        onChange={(value) => setField("desiredDeliveryDate", value)}
      />

      <label className="flex items-center gap-2 py-1 cursor-pointer">
        <input
          type="checkbox"
          checked={!!order.onSiteHelp}
          onChange={(e) => setField("onSiteHelp", e.target.checked)}
        />
        <span>We would like help from Diflex on site to set up the workplace</span>
      </label>

      {order.onSiteHelp && (
        <InputField
          label="Notes for the on-site visit"
          value={order.onSiteHelpNotes || ""}
          onChange={(e) => setField("onSiteHelpNotes", e.target.value)}
          placeholder="Floor, room, contact person, preferred time"
        />
      )}

      <InputField
        label="Deliver to"
        value={order.deliverTo || ""}
        onChange={(e) => setField("deliverTo", e.target.value)}
        placeholder="Name of the person receiving the delivery"
      />

      <InputField
        label="Delivery address"
        value={order.deliveryAddress || ""}
        onChange={(e) => setField("deliveryAddress", e.target.value)}
        placeholder="Street, postal code, city"
        type="text-area"
      />
    </div>
  );
};

export default HardwareOrderSection;
